import { useState, 
         useRef, 
         useEffect,
         SyntheticEvent
       }                                              from 'react'
import ReactCrop, 
       { centerCrop,
         makeAspectCrop,
         Crop,
         PixelCrop
       }                                              from 'react-image-crop'
import { canvasPreview }                              from './CanvasPreview'
import { useDebounceEffect }                          from 'hooks'
import { Dialog, 
         Typography, 
         Alert, 
         CircularProgress, 
         Box 
       }                                            from '@mui/material'
import { BglCloseButton, 
         BglPrimaryButton 
       }                                            from 'components/button/BglButtons'
import { useGlobalConfig }                          from 'context/GlobalContext'
import 'react-image-crop/dist/ReactCrop.css'

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 100,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

export interface ImageCropComponentProps {
  open        : boolean
  srcUrl      : string
  file        ?: File
  minWidth    : number
  minHeight   : number
  aspectRatio : number
  imgMaxSize  : number // bytes
  onClose     : (blob ?: Blob) => void
}

export default function ImageCropDialog(props : ImageCropComponentProps) {
  
  const { rc } = useGlobalConfig()
  const { onClose, open } = props;

  const handleClose = (blob ?: Blob) => {
    onClose(blob);
  };

  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [scale, setScale] = useState(1)
  const [rotate, setRotate] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Handle both file and URL sources
  useEffect(() => {
    const loadImage = async () => {
      setIsLoading(true);
      setError(null);
      setImageUrl(null);

      try {
        if (props.file) {
          // Handle file upload
          if (props.file.size > props.imgMaxSize) {
            throw new Error(`File size exceeds ${props.imgMaxSize / (1024 * 1024)}MB limit`);
          }

          if (!props.file.type.startsWith('image/')) {
            throw new Error('Please upload an image file');
          }

          // Use data URL instead of blob URL to avoid CSP issues
          const url = await rc.utils.blobToDataURI(props.file);
          setImageUrl(url);
        } else if (props.srcUrl) {
          const proxyUrl = `/global/proxy-image?url=${encodeURIComponent(props.srcUrl)}`
          const blob = await rc.cmsApiClient.getBlob(proxyUrl)
          if (blob.size > props.imgMaxSize) {
            throw new Error(`Image size exceeds ${props.imgMaxSize / (1024 * 1024)}MB limit`)
          }

          // Use data URL instead of blob URL to avoid CSP issues
          const url = await rc.utils.blobToDataURI(blob);
          setImageUrl(url);
        } else {
          throw new Error('No image source provided');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load image');
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      loadImage();
    }
  }, [props.file, props.srcUrl, open]);

  function onImageLoad(e: SyntheticEvent<HTMLImageElement>) {
    if (props.aspectRatio) {
      const { width, height } = e.currentTarget
      const initialCrop = centerAspectCrop(width, height, props.aspectRatio)
      setCrop(initialCrop)
      
      // Convert the initial crop to pixel crop for immediate preview
      const pixelCrop: PixelCrop = {
        x: (initialCrop.x / 100) * width,
        y: (initialCrop.y / 100) * height,
        width: (initialCrop.width / 100) * width,
        height: (initialCrop.height / 100) * height,
        unit: 'px'
      }
      setCompletedCrop(pixelCrop)
    }
  }

  async function onDownloadCropClick() {
    const image = imgRef.current
    const previewCanvas = previewCanvasRef.current
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error('Crop canvas does not exist')
    }

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    // Calculate the natural size of the cropped area
    const naturalCropWidth = completedCrop.width * scaleX
    const naturalCropHeight = completedCrop.height * scaleY

    // Define maximum dimensions (adjust these values as needed)
    const maxWidth = 1920  // Maximum width in pixels
    const maxHeight = 1080 // Maximum height in pixels

    // Calculate scaling factor to fit within max dimensions while maintaining aspect ratio
    const scaleFactor = Math.min(
      maxWidth / naturalCropWidth,
      maxHeight / naturalCropHeight,
      1 // Don't upscale
    )

    // Use the scaled dimensions for the output canvas
    const outputWidth = Math.floor(naturalCropWidth * scaleFactor)
    const outputHeight = Math.floor(naturalCropHeight * scaleFactor)

    const offscreen = new OffscreenCanvas(outputWidth, outputHeight)
    const ctx = offscreen.getContext('2d')
    if (!ctx) {
      throw new Error('No 2d context')
    }

    // Set high quality rendering
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      outputWidth,
      outputHeight,
    )

    // Calculate dynamic quality based on image size
    const pixelCount = outputWidth * outputHeight
    let quality: number

    if (pixelCount <= 500000) { // Small images (e.g., 707x707 or smaller)
      quality = 0.95 // High quality for small images
    } else if (pixelCount <= 1000000) { // Medium images (e.g., 1000x1000 or smaller)
      quality = 0.9 // Good quality for medium images
    } else if (pixelCount <= 2000000) { // Large images (e.g., 1414x1414 or smaller)
      quality = 0.8 // Balanced quality for large images
    } else { // Very large images
      quality = 0.7 // Lower quality for very large images to keep file size manageable
    }

    const blob = await offscreen.convertToBlob({
      type: imgRef.current?.src.match(/\.([^.]+)$/)?.[1] || 'jpeg', 
      quality: quality,
    })

    handleClose(blob)
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate,
        )
      }
    },
    100,
    [completedCrop, scale, rotate],
  )

  return (
    <Dialog 
      disableEscapeKeyDown 
      fullWidth 
      maxWidth='md'
      onClose={() => handleClose()} 
      open={open}>
      <div style={{padding : 20, paddingTop : 10}}>
        <Typography variant='bglTitle4'>Crop Image</Typography>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {imageUrl && !error && !isLoading && (
          <>
            <div style={{marginBottom : 20}}>
              <div>
                <label htmlFor="scale-input">Scale: </label>
                <input
                  id="scale-input"
                  type="number"
                  step="0.1"
                  value={scale}
                  onChange={(e) => setScale(Number(e.target.value))}
                />
              </div>
              <div style={{ marginTop: 10}}>
                <label htmlFor="rotate-input">Rotate: </label>
                <input
                  id="rotate-input"
                  type="number"
                  value={rotate}
                  onChange={(e) =>
                    setRotate(Math.min(180, Math.max(-180, Number(e.target.value))))
                  }
                />
              </div>
            </div>

            <ReactCrop
              minWidth={props.minWidth}
              minHeight={props.minHeight}
              aspect={props.aspectRatio}
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imageUrl}
                style={{ backgroundColor: 'gray', transform: `scale(${scale}) rotate(${rotate}deg)` }}
                onLoad={onImageLoad}
              />
            </ReactCrop>

            {!!completedCrop && (
              <>
                <div>
                  <canvas
                    ref={previewCanvasRef}
                    style={{
                      backgroundColor: 'gray',
                      border: '1px solid black',
                      objectFit: 'contain',
                      width: completedCrop.width,
                      height: completedCrop.height,
                    }}
                  />
                </div>
                <div style={{ display: 'flex', marginTop: 20, justifyContent: 'end'}}>
                  <BglCloseButton onClick={() => props.onClose()} sx={{ marginRight: '10px' }}>
                      CLOSE 
                  </BglCloseButton>
                  <BglPrimaryButton onClick={onDownloadCropClick}>
                      SAVE
                  </BglPrimaryButton>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Dialog>
  )
}